import { useState } from 'react';
import { createPortal } from 'react-dom';
import { parseAcknowledgement } from '@/utils/outreach';
import { Hand } from 'lucide-react';

interface OutreachAcknowledgementDialogProps {
  text: string;
  onAccept: () => Promise<void>;
  onCancel: () => void;
}

/** "Before you begin": shown once per version before a leader's first hello. */
export function OutreachAcknowledgementDialog({
  text,
  onAccept,
  onCancel,
}: OutreachAcknowledgementDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const { intro, promises } = parseAcknowledgement(text);

  const accept = async () => {
    setBusy(true);
    setError(false);
    try {
      await onAccept();
    } catch (e) {
      console.error('Failed to save acknowledgement:', e);
      setError(true);
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onCancel} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Before you begin"
        className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl p-5"
      >
        <h3 className="text-lg font-semibold text-slate-800 font-['Merriweather',serif]">
          Before you begin
        </h3>
        <p className="text-sm text-slate-500">{intro || 'A few promises we make to each other'}</p>
        <ul className="mt-3 space-y-3">
          {promises.map((p) => (
            <li key={p} className="flex gap-3 text-sm text-slate-700">
              <Hand size={18} className="shrink-0 mt-0.5 text-[#8e4585]" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
        {error && (
          <p role="alert" className="mt-3 text-sm text-rose-600">
            That didn&apos;t save. Please try again.
          </p>
        )}
        <button
          type="button"
          onClick={accept}
          disabled={busy}
          className="mt-4 w-full py-3 rounded-full text-white font-semibold bg-gradient-to-r from-[#8e4585] to-[#63345e] disabled:opacity-60"
        >
          {busy ? 'Saving…' : 'I understand'}
        </button>
        <p className="mt-2 text-center text-xs text-slate-400">
          Shown once. Reviewable any time in Settings.
        </p>
      </div>
    </div>,
    document.body
  );
}
